import React, { useContext, useEffect, useReducer, useState } from 'react';
import api from '../api';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import { Store } from '../Store';
import MessageBox from '../components/general/MessageBox';
import Card from 'react-bootstrap/Card';
import { Container, Modal, Form, DropdownButton } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import Pagination from '../components/general/Pagination';
import { getError } from '../utils';
import LoadingLogo from '../components/general/LoadingLogo';
import { useTranslation } from 'react-i18next';
import {
  findSoldOutLayouts,
  isDesignSoldOut,
  purchaseDisabled,
  savedDesignCartItem,
} from '../utils/savedDesigns';
import { productNameOfLayout } from '../utils/customizerLayout';
import { useInFlightGuard } from '../utils/useInFlightGuard';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true, error: '' }; // a kept error would outlive the next successful fetch (the render checks it after loading)
    case 'FETCH_SUCCESS':
      return {
        ...state,
        communityDesigns: action.payload.communityDesignsData,
        page: action.payload.page,
        pages: action.payload.pages,
        loading: false,
      };
    case 'FETCH_LAYOUTS_SUCCESS': {
      // Create a Set of sold-out profiles for each layout
      const soldOutLayouts = findSoldOutLayouts(action.payload.layouts);

      // Attach soldOutLayouts to the state for efficient checks
      return {
        ...state,
        layouts: action.payload.layouts,
        products: action.payload.products,
        soldOutLayouts,
        loading: false,
      };
    }
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };

    case 'CREATE_LIKE_SUCCESS': {
      const { designId } = action.payload;

      const updateDesigns = (designs) =>
        designs.map((design) =>
          design._id === designId
            ? {
                ...design,
                liked: true,
                metadata: {
                  ...design.metadata,
                  numOfLikes: (design.metadata.numOfLikes || 0) + 1, // ensures that numOfLikes is incremented even if it was initially undefined or null.
                },
              }
            : design
        );

      return {
        ...state,
        communityDesigns: updateDesigns(state.communityDesigns),
      };
    }
    case 'REMOVE_LIKE_SUCCESS': {
      const { designId } = action.payload;

      const updateDesigns = (designs) =>
        designs.map((design) =>
          design._id === designId
            ? {
                ...design,
                liked: false,
                metadata: {
                  ...design.metadata,
                  numOfLikes: (design.metadata.numOfLikes || 1) - 1, // ensures that numOfLikes is decremented properly.
                },
              }
            : design
        );

      return {
        ...state,
        communityDesigns: updateDesigns(state.communityDesigns),
      };
    }

    default:
      return state;
  }
};

export default function CommunityDesignsScreen() {
  const [
    {
      loading,
      error,
      communityDesigns,
      pages,
      layouts,
      products,
      soldOutLayouts,
    },
    dispatch,
  ] = useReducer(reducer, {
    loading: true,
    error: '',
    layouts: [],
    products: [],
    communityDesigns: [],
    soldOutLayouts: new Set(),
  });

  const { t } = useTranslation();
  const navigate = useNavigate();

  const { state, dispatch: ctxDispatch } = useContext(Store);
  const { userInfo } = state; // gets the userInfo out of the application state, so that the users token can be used to authorize the fetch request
  const [show, setShow] = useState(false);
  const [selectedThumbnail, setSelectedThumbnail] = useState(''); // Store the thumbnail URL
  const [layoutMode, setLayoutMode] = useState(3);
  const [screenSize, setScreenSize] = useState('lg'); // Track current screen size
  const { inFlight: likeRequestsInProgress, run: runLikeRequest } =
    useInFlightGuard(); // the designs a like request is running for
  const [imagesLoading, setImagesLoading] = useState(false);

  const { search } = useLocation(); // gets the query parameters of the URL and saves them in the variable called 'search'
  const sp = new URLSearchParams(search); // creates a new instance of the URLSearchParams class, passing in the search string, wich allows to apply several methods to work with the query parameters
  const page = sp.get('page') || 1; // Retrieves the value of the 'page' query parameter (the number on wich page the user is on)
  const searchQuery = sp.get('search') || ''; // Retrieves the 'search' parameter from the URL query string; defaults to an empty string if not present
  const [searchInput, setSearchInput] = useState(searchQuery); // Local state for search input
  const [sort, setSort] = useState('popular'); // Sorting selection
  const [selectedLayout, setSelectedLayout] = useState('all'); // Layout filter
  const [selectedProfile, setSelectedProfile] = useState('all'); // Profile filter

  function preloadImage(url) {
    return new Promise((resolve) => {
      if (!url) {
        // If there's no URL, resolve immediately so we don't stall the entire loading
        return resolve();
      }
      const img = new Image();
      img.src = url;
      img.onload = () => resolve();
      img.onerror = () => resolve();
    });
  } // preloadImage helper

  useEffect(() => {
    const updateScreenSize = () => {
      if (window.matchMedia('(max-width: 575px)').matches) {
        setScreenSize('xs'); // Extra Small
      } else if (window.matchMedia('(max-width: 767px)').matches) {
        setScreenSize('sm'); // Small
      } else if (window.matchMedia('(max-width: 991px)').matches) {
        setScreenSize('md'); // Medium
      } else {
        setScreenSize('lg'); // Large
      }
    };

    updateScreenSize(); // Set initial size
    window.addEventListener('resize', updateScreenSize); // Update on resize
    return () => window.removeEventListener('resize', updateScreenSize); // Cleanup
  }, []); // Determine screen size dynamically

  useEffect(() => {
    if (screenSize === 'xs' && layoutMode !== 1) {
      setLayoutMode(1);
    } else if (screenSize === 'sm' && layoutMode > 2) {
      setLayoutMode(2);
    } else if (screenSize === 'md' && layoutMode > 3) {
      setLayoutMode(3);
    }
  }, [screenSize, layoutMode]); // Reset layout mode if it's invalid for the current screen size

  const getColClasses = () => {
    switch (layoutMode) {
      case 1:
        return 'col-12';
      case 2:
        return 'col-sm-6';
      case 3:
        return 'col-md-4';
      case 4:
        return 'col-lg-3';
      default:
        return 'col-lg-4 col-md-6 col-sm-12';
    }
  }; // Generate column classes based on layout mode

  const handleShow = (thumbnail) => {
    setSelectedThumbnail(thumbnail); // Update selected thumbnail
    setShow(true); // Show modal
  };
  const handleClose = () => setShow(false);

  useEffect(() => {
    const fetchData = async () => {
      dispatch({ type: 'FETCH_REQUEST' });

      try {
        const { data } = await api.get(`/api/products/custom-keycap-sets`);

        const { products, layouts } = data;

        dispatch({
          type: 'FETCH_LAYOUTS_SUCCESS',
          payload: { products, layouts },
        });
      } catch (err) {
        dispatch({
          type: 'FETCH_FAIL',
          payload: getError(err),
        });
      }
    }; // fetches the products and layouts from the backend

    fetchData();
  }, [userInfo]);

  useEffect(() => {
    const fetchData = async () => {
      dispatch({ type: 'FETCH_REQUEST' });

      try {
        const { data } = await api.get(
          `/api/designs/community-designs?page=${page}&search=${encodeURIComponent(
            searchQuery
          )}&sort=${sort}&layout=${selectedLayout}` // encodeURIComponent: encodes the searchQuery to ensure that special characters are properly formatted in the URL, preventing errors and ensuring accurate query handling
        );

        const communityDesignsData = data.designs;

        let updatedCommunityDesigns = communityDesignsData;

        if (userInfo) {
          const likesResponse = await api.get(`/api/likes/mine`);

          const likesData = likesResponse.data;
          const likedDesignIds = new Set(
            likesData.map((like) => like.designId)
          ); // creating a Set of liked design IDs for easy lookup

          updatedCommunityDesigns = communityDesignsData.map((design) => ({
            ...design,
            liked: likedDesignIds.has(design._id),
          })); // adding a liked field to the community designs (value is true, if a Like document exists for this design, otherwise false)
        }

        dispatch({
          type: 'FETCH_SUCCESS',
          payload: {
            ...data,
            communityDesignsData: updatedCommunityDesigns,
          },
        });
      } catch (err) {
        dispatch({
          type: 'FETCH_FAIL',
          payload: getError(err),
        });
      }
    }; // fetches the community designs from the backend

    fetchData();
  }, [page, userInfo, searchQuery, sort, selectedLayout]);

  useEffect(() => {
    if (!loading && communityDesigns.length > 0) {
      setImagesLoading(true);

      const urls = communityDesigns
        .map((design) => design.metadata?.thumbnail)
        .filter(Boolean);

      Promise.all(urls.map((url) => preloadImage(url)))
        .then(() => setImagesLoading(false))
        .catch((err) => {
          console.error('Image preload failed:', err);
          setImagesLoading(false);
        });
    } else {
      setImagesLoading(false);
    }
  }, [loading, communityDesigns]); // After data is fetched, do a second effect to preload images

  const handleLike = async (
    designId,
    isLiked,
    designOwnerId,
    currentUserId
  ) => {
    // 1. If user is design's owner, or there's no user logged in:
    if (!userInfo) {
      navigate('/signup?redirect=/community-designs');
      return;
    }
    if (designOwnerId === currentUserId) {
      toast(t('community-designs-screen-like-warning'));
      return;
    }

    // 2. Prevent multiple clicks and 3. mark this design as "in progress" (the hook, which 5. removes the mark again):
    await runLikeRequest(designId, async () => {
      try {
        // 4. Actually do the Like/Unlike request
        if (isLiked) {
          // UNLIKE
          await api.delete('/api/likes', {
            data: { designId },
          });
          dispatch({ type: 'REMOVE_LIKE_SUCCESS', payload: { designId } });
        } else {
          // LIKE
          await api.post('/api/likes', { designId });
          dispatch({ type: 'CREATE_LIKE_SUCCESS', payload: { designId } });
        }
      } catch (err) {
        console.error('Error:', err);
        toast.error(getError(err)); // no fail action: the reducer has no case for one (a dispatch without a case was deleted Oct 2026)
      }
    });
  };

  const submitHandler = (e) => {
    e.preventDefault();
    const sanitizedInput = searchInput.trim(); // Keep spaces between words but remove leading/trailing spaces
    navigate(
      sanitizedInput
        ? `/community-designs?search=${encodeURIComponent(
            sanitizedInput
          )}&page=1`
        : `/community-designs?page=1`
    ); // Navigates to the CommunityDesignsScreen with the search query and resets to page 1 if sanitizedInput exists; otherwise, navigates to page 1 without any search filter
  };

  const addToCartHandler = (design) => {
    const { reason, item } = savedDesignCartItem(design, products, userInfo, {
      withCustomLayoutMark: false,
    }); // a community item carries no custom-layout mark (a public design is never one)

    if (reason === 'uploaded-icons') {
      toast.error(t('community-designs-screen-design-cannot-be-added-to-cart'));
      return;
    } // prevent adding community designs with uploaded icons to the cart unless the signed-in user owns those uploaded icons

    if (reason === 'no-product') {
      toast.error(t('community-designs-screen-add-to-cart-warning'));
      return;
    }

    ctxDispatch({
      type: 'CART_ADD_ITEM',
      payload: item,
    });
    toast.success(t('community-designs-screen-add-to-cart-success'));
  };

  const getProductName = (layout) => productNameOfLayout(products, layout._id); // helper function to get the product name based on a layout (the layout filter maps the layouts list, so the guard for no layout went with the merge into the customizer's helper: a chain 8 review)

  return (
    <Container>
      <div>
        <title>{t('community-designs-screen-helmet')}</title>

        <h1 className="screen-heading">
          {t('community-designs-screen-heading')}
        </h1>

        {/* Search Form */}
        <Form onSubmit={submitHandler} className="community-designs-search">
          <div style={{ width: '100%' }}>
            <Form.Control
              type="text"
              className="mobile-device-compatible-search"
              placeholder={t('community-designs-screen-search-bar')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label={t('community-designs-screen-search-bar')}
            />
          </div>
          <Button type="submit" variant="secondary">
            {t('community-designs-screen-search-button')}
          </Button>
        </Form>

        <Row className="community-designs-filters">
          <Col className="d-flex justify-content-between">
            <div className="d-flex align-items-center">
              {/* Sorting dropdown */}
              <Form.Group>
                <Form.Select
                  aria-label="Sort designs"
                  style={{ width: '120px' }}
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                >
                  <option value="popular">
                    {t('community-designs-screen-sort-popular')}
                  </option>
                  <option value="newest">
                    {t('community-designs-screen-sort-newest')}
                  </option>
                </Form.Select>
              </Form.Group>

              <DropdownButton
                id="dropdown-basic-button"
                variant="none"
                className="no-css"
                drop="down-centered"
                title={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    className="bi funnel"
                    viewBox="0 0 16 16"
                  >
                    <path d="M1.5 1.5A.5.5 0 0 1 2 1h12a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.128.334L10 8.692V13.5a.5.5 0 0 1-.342.474l-3 1A.5.5 0 0 1 6 14.5V8.692L1.628 3.834A.5.5 0 0 1 1.5 3.5zm1 .5v1.308l4.372 4.858A.5.5 0 0 1 7 8.5v5.306l2-.666V8.5a.5.5 0 0 1 .128-.334L13.5 3.308V2z" />
                  </svg>
                }
              >
                <div style={{ padding: '5px 10px' }}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: '12px' }}>
                      {t('community-designs-screen-layout-filter')}
                    </Form.Label>

                    <Form.Select
                      aria-label={t(
                        'community-designs-screen-layout-filter-lable'
                      )}
                      style={{ width: '200px' }}
                      value={selectedLayout}
                      onChange={(e) => setSelectedLayout(e.target.value)}
                    >
                      <option value="all">
                        {t('community-designs-screen-default-layout-filter')}
                      </option>
                      {layouts.map((layout) => (
                        <option key={layout._id} value={layout._id}>
                          {getProductName(layout)}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group>
                    <Form.Label style={{ fontSize: '12px' }}>
                      {t('community-designs-screen-profile-filter')}
                    </Form.Label>

                    <Form.Select
                      aria-label={t(
                        'community-designs-screen-profile-filter-lable'
                      )}
                      style={{ width: '200px' }}
                      value={selectedProfile}
                      onChange={(e) => setSelectedProfile(e.target.value)}
                    >
                      <option value="XDA">XDA</option>
                    </Form.Select>
                  </Form.Group>
                </div>
              </DropdownButton>
            </div>

            <div className="d-flex align-items-center">
              {/* Layout Selector */}
              {screenSize !== 'xs' && (
                <Button
                  key={1}
                  variant={layoutMode === 1 ? 'dark' : 'light'}
                  className="me-2"
                  onClick={() => setLayoutMode(1)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="10"
                    height="10"
                    fill="currentColor"
                    className="bi grid-1"
                    viewBox="0 0 5 5"
                  >
                    <rect
                      key={'1'}
                      x={0.5}
                      y={0.5}
                      width="4"
                      height="4"
                      rx="1"
                      fill="currentColor"
                    />
                  </svg>
                </Button>
              )}
              {screenSize !== 'xs' && (
                <Button
                  key={2}
                  variant={layoutMode === 2 ? 'dark' : 'light'}
                  className={screenSize === 'sm' ? '' : 'me-2'}
                  onClick={() => setLayoutMode(2)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="10"
                    fill="currentColor"
                    className="bi grid-2"
                    viewBox="0 0 10 5"
                  >
                    <rect
                      key={'1'}
                      x={0.5}
                      y={0.5}
                      width="4"
                      height="4"
                      rx="1"
                      fill="currentColor"
                    />
                    <rect
                      key={'2'}
                      x={5.5}
                      y={0.5}
                      width="4"
                      height="4"
                      rx="1"
                      fill="currentColor"
                    />
                  </svg>
                </Button>
              )}
              {screenSize === 'lg' || screenSize === 'md' ? (
                <Button
                  key={3}
                  variant={layoutMode === 3 ? 'dark' : 'light'}
                  className={screenSize === 'md' ? '' : 'me-2'}
                  onClick={() => setLayoutMode(3)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="30"
                    height="10"
                    fill="currentColor"
                    className="bi grid-3"
                    viewBox="0 0 15 5"
                  >
                    <rect
                      key={'1'}
                      x={0.5}
                      y={0.5}
                      width="4"
                      height="4"
                      rx="1"
                      fill="currentColor"
                    />
                    <rect
                      key={'2'}
                      x={5.5}
                      y={0.5}
                      width="4"
                      height="4"
                      rx="1"
                      fill="currentColor"
                    />
                    <rect
                      key={'3'}
                      x={10.5}
                      y={0.5}
                      width="4"
                      height="4"
                      rx="1"
                      fill="currentColor"
                    />
                  </svg>
                </Button>
              ) : null}
              {screenSize === 'lg' && (
                <Button
                  key={4}
                  variant={layoutMode === 4 ? 'dark' : 'light'}
                  className={screenSize === 'lg' ? '' : 'me-2'}
                  onClick={() => setLayoutMode(4)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="40"
                    height="10"
                    fill="currentColor"
                    className="bi grid-4"
                    viewBox="0 0 20 5"
                  >
                    <rect
                      key={'1'}
                      x={0.5}
                      y={0.5}
                      width="4"
                      height="4"
                      rx="1"
                      fill="currentColor"
                    />
                    <rect
                      key={'2'}
                      x={5.5}
                      y={0.5}
                      width="4"
                      height="4"
                      rx="1"
                      fill="currentColor"
                    />
                    <rect
                      key={'3'}
                      x={10.5}
                      y={0.5}
                      width="4"
                      height="4"
                      rx="1"
                      fill="currentColor"
                    />
                    <rect
                      key={'4'}
                      x={15.5}
                      y={0.5}
                      width="4"
                      height="4"
                      rx="1"
                      fill="currentColor"
                    />
                  </svg>
                </Button>
              )}
            </div>
          </Col>
        </Row>

        {loading || imagesLoading ? (
          <div className="overlay">
            <LoadingLogo />
          </div>
        ) : error ? (
          <MessageBox className="danger-alert">{error}</MessageBox>
        ) : (
          <div>
            <Row>
              <Col md={12}>
                <Row>
                  {communityDesigns.length === 0 ? (
                    <div className="w-100">
                      <MessageBox className="default-alert">
                        {t('community-designs-screen-no-designs-available')}
                      </MessageBox>
                    </div>
                  ) : null}

                  {communityDesigns.map((design) => {
                    const isSoldOut = isDesignSoldOut(soldOutLayouts, design); // checks if this design's selected layout/profile is sold out

                    const isPurchaseDisabled = purchaseDisabled(
                      design,
                      userInfo
                    ); // disables purchase for other users if this design contains visible user-uploaded icons (utils/savedDesigns.js, the cart item's rule; a copy here until chain 10, Oct 2026)

                    return (
                      <Col
                        key={design._id}
                        className={`community-design-card ${getColClasses()}`}
                      >
                        <Card className="design-card">
                          {/* don't apply padding to the Card component, as it will make the imgaes slightly blurry (instead apply it to the Col) */}
                          <Card.Body className={`layout-mode-${layoutMode}`}>
                            <Row className="design-info">
                              <Col className="d-flex justify-content-between align-items-center">
                                <div className="design-name">
                                  {design.name}
                                  <span className="username">
                                    {t('community-designs-screen-by')}{' '}
                                    {design.metadata.username}
                                  </span>
                                </div>

                                <div className="likes">
                                  <Button
                                    variant="none"
                                    className="no-css like-button-reset"
                                    onClick={() =>
                                      handleLike(
                                        design._id,
                                        design.liked,
                                        design.metadata.userId,
                                        userInfo ? userInfo.userId : null
                                      )
                                    }
                                    disabled={
                                      !design ||
                                      likeRequestsInProgress.has(design._id)
                                    } // Disable if no design or if like request for this design is in progress
                                  >
                                    {design.liked ? (
                                      <div className="d-flex justify-content-evenly align-items-center">
                                        <Card.Text>
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="#fe4a49"
                                            className="bi bi-heart-fill design-like-svg"
                                            viewBox="0 0 16 16"
                                          >
                                            <path
                                              fillRule="evenodd"
                                              d="M8 1.314C12.438-3.248 23.534 4.735 8 15-7.534 4.736 3.562-3.248 8 1.314"
                                            />
                                          </svg>

                                          <span className="no-wrap mx-2 liked-count">
                                            {design.metadata.numOfLikes}
                                          </span>
                                        </Card.Text>
                                      </div>
                                    ) : (
                                      <div className="d-flex justify-content-evenly align-items-center">
                                        <Card.Text>
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="#333333"
                                            className="bi bi-heart design-like-svg"
                                            viewBox="0 0 16 16"
                                          >
                                            <path d="m8 2.748-.717-.737C5.6.281 2.514.878 1.4 3.053c-.523 1.023-.641 2.5.314 4.385.92 1.815 2.834 3.989 6.286 6.357 3.452-2.368 5.365-4.542 6.286-6.357.955-1.886.838-3.362.314-4.385C13.486.878 10.4.28 8.717 2.01zM8 15C-7.333 4.868 3.279-3.04 7.824 1.143q.09.083.176.171a3 3 0 0 1 .176-.17C12.72-3.042 23.333 4.867 8 15" />
                                          </svg>

                                          <span className="no-wrap mx-2">
                                            {design.metadata.numOfLikes}
                                          </span>
                                        </Card.Text>
                                      </div>
                                    )}
                                  </Button>
                                </div>
                              </Col>
                            </Row>

                            {/* while the thumbnail can be enlarged it sits in a button, so the zoom is reachable by keyboard (the button takes its accessible name from the image's alt text): */}
                            {screenSize !== 'xs' && layoutMode !== 1 ? (
                              <button
                                type="button"
                                className="a11y-unstyled-button"
                                onClick={() =>
                                  handleShow(design.metadata.thumbnail)
                                }
                              >
                                <img
                                  src={design.metadata.thumbnail}
                                  alt={design.name}
                                  className="img-fluid thumbnail-community-designs-scale"
                                />
                              </button>
                            ) : (
                              <img
                                src={design.metadata.thumbnail}
                                alt={design.name}
                                className={
                                  layoutMode === 1
                                    ? 'img-fluid'
                                    : 'img-fluid thumbnail-community-designs-scale'
                                }
                              />
                            )}

                            <div>
                              <div className="design-card-divider"></div>

                              <div className="d-flex justify-content-center">
                                <Button
                                  variant="light"
                                  className={`add-to-cart-button ${
                                    isPurchaseDisabled
                                      ? 'invisible-button-space'
                                      : ''
                                  }`}
                                  onClick={() => addToCartHandler(design)}
                                  disabled={isSoldOut || isPurchaseDisabled}
                                >
                                  {isSoldOut
                                    ? t('community-designs-screen-sold-out')
                                    : t('community-designs-screen-add-to-cart')}
                                </Button>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    );
                  })}

                  <Modal
                    show={show}
                    onHide={handleClose}
                    centered
                    className="thumbnail-modal"
                  >
                    <Modal.Body>
                      <img src={selectedThumbnail} alt="Selected Thumbnail" />
                    </Modal.Body>

                    <Modal.Footer>
                      <Button variant="secondary" onClick={handleClose}>
                        {t('community-designs-screen-close-button')}
                      </Button>
                    </Modal.Footer>
                  </Modal>

                  <Pagination
                    pages={pages}
                    page={Number(page)}
                    baseUrl="/community-designs"
                    search={searchQuery}
                  />
                </Row>
              </Col>
            </Row>
          </div>
        )}
      </div>
    </Container>
  );
}
